import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { store } from "../../../../../store";
import {
  editSampleShipment,
  getSampleShipmentByReference,
} from "../../../../../redux/actions/sampleShipment";
import useShipmentCenter from "./shared/useShipmentCenter";
import { getStudyInfo } from "../../../../../redux/actions/study";
import {
  deleteSampleShipmentReception,
  upsertSampleShipmentReception,
} from "../../../../../services/sampleShipmentReceptionService";
import { getTemperaturesByStudy } from "../../../../../services/temperatureService";
import { showErrorAlert } from "../../../../ui/utils/AlertUtils";
import { showSuccessToast } from "../../../../../shared/ui/toast/Toast";
import { printSampleShipmentReception } from "../../../../ui/SamplePdfGenerator";
import PageHeader from "../../../../ui/PageHeader";
import SampleShipmentSteps from "./shared/SampleShipmentSteps";
import StudyCodeLabelHelper from "./shared/StudyCodeLabelHelper";
import SampleShipmentAudit from "./shared/SampleShipmentAudit";

const sectionSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 2,
  borderColor: "divider",
};

// const airwaybillDocumentActionsEnabled = false;

const tableSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  overflow: "hidden",
};

const ROOM_TEMPERATURE_LABEL = "RT (15°C / 25°C)";

const normalizeShippingTemperature = (temperature) => {
  const value = String(temperature || "").trim();
  const normalized = value.toLowerCase();

  if (
    normalized === "room temperature" ||
    normalized === "rt" ||
    normalized === "rt (15°c / 25°c)" ||
    normalized === "rt (15c / 25c)"
  ) {
    return ROOM_TEMPERATURE_LABEL;
  }

  return value;
};

const getSampleAnalyses = (sample = {}) =>
  Array.isArray(sample.analyses) ? sample.analyses : [];

const getSampleTemperature = (sample = {}) => {
  const analysisTemperature = sample.analysis?.temperature;
  if (typeof analysisTemperature === "string") {
    return normalizeShippingTemperature(analysisTemperature);
  }
  if (analysisTemperature?.name) {
    return normalizeShippingTemperature(analysisTemperature.name);
  }

  const temperatures = getSampleAnalyses(sample)
    .map((analysis) => {
      if (typeof analysis?.temperature === "string") {
        return normalizeShippingTemperature(analysis.temperature);
      }
      return normalizeShippingTemperature(
        analysis?.temperature?.name || analysis?.Temperature?.name,
      );
    })
    .filter(Boolean);

  return [...new Set(temperatures)].join(", ") || "Unknown";
};

const getTemperatureSettingName = (temperature = {}) => {
  if (typeof temperature === "string") {
    return normalizeShippingTemperature(temperature);
  }

  return normalizeShippingTemperature(
    temperature.name || temperature.temperatureName || temperature.label,
  );
};

const uniqueTemperatures = (temperatures = []) => {
  const seen = new Set();

  return temperatures
    .map(normalizeShippingTemperature)
    .filter(Boolean)
    .filter((temperature) => {
      const key = temperature.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getSampleQuantitySent = (sample = {}) =>
  sample.quantitySent ?? sample.totalSamples ?? sample.quantity ?? 0;

const SectionHeader = ({ title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 1.5,
      mb: 2.5,
    }}>
    <Box>
      <Typography
        variant="h6"
        sx={{ color: "primary.main", fontWeight: 700, lineHeight: 1.25 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

const ReadOnlyField = ({ label, value }) => (
  <TextField
    label={label}
    value={value || "-"}
    InputProps={{ readOnly: true }}
    fullWidth
  />
);

const CheckboxField = ({ label, checked, onChange }) => (
  <Box
    sx={{
      minHeight: 56,
      px: 1.5,
      display: "flex",
      alignItems: "center",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
    }}>
    <FormControlLabel
      sx={{ m: 0 }}
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={label}
    />
  </Box>
);

const buildShippingTemperatureSummary = (
  samples = [],
  studyTemperatures = [],
) => {
  const totals = uniqueTemperatures(studyTemperatures).reduce(
    (acc, temperature) => ({ ...acc, [temperature]: 0 }),
    {}
  );

  samples.forEach((sample) => {
    const temperature = normalizeShippingTemperature(getSampleTemperature(sample));
    if (temperature && totals[temperature] !== undefined) {
      totals[temperature] += Number(getSampleQuantitySent(sample)) || 0;
    }
  });

  return Object.entries(totals).map(([temperature, total]) => ({
    temperature,
    total,
  }));
};

const toDateOrNull = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
};

const fromDateTimeInputValue = (value) => {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date.toDate() : null;
};

const formatTrackingName = (user) =>
  [
    user?.firstname,
    user?.name,
  ]
    .filter(Boolean)
    .join(" ");

const toBooleanValue = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "1", "yes", "on"].includes(normalized);
  }
  return Boolean(value);
};

const normalizeReceptionValue = (value) =>
  value === undefined || value === null ? "" : String(value);

const normalizeReceptionDateTime = (value) => {
  if (!value) return "";

  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
};

const normalizeReceptionTracking = (tracking = {}) => ({
  name: normalizeReceptionValue(tracking.name),
  email: normalizeReceptionValue(tracking.email),
  date: normalizeReceptionDateTime(tracking.date),
});

const buildComparableReceptionSnapshot = (draft = {}) => ({
  storageDatetime: normalizeReceptionDateTime(draft.storageDatetime),
  storageChamber: normalizeReceptionValue(draft.storageChamber),
  storageShelf: normalizeReceptionValue(draft.storageShelf),
  comments: normalizeReceptionValue(draft.comments),
  temperatureProbe: toBooleanValue(draft.temperatureProbe),
  sonde: normalizeReceptionValue(draft.sonde),
  conforme: toBooleanValue(draft.conforme),
  items: (draft.items || [])
    .map((item) => ({
      shippingTemperature: normalizeShippingTemperature(
        item.shippingTemperature,
      ),
      quantityReceived: normalizeReceptionValue(item.quantityReceived),
      conforme: toBooleanValue(item.conforme),
      comments: normalizeReceptionValue(item.comments),
    }))
    .sort((left, right) =>
      left.shippingTemperature.localeCompare(right.shippingTemperature),
    ),
  requestTracking: normalizeReceptionTracking(draft.requestTracking),
  confirmTracking: normalizeReceptionTracking(draft.confirmTracking),
  receiveTracking: normalizeReceptionTracking(draft.receiveTracking),
});

const areReceptionSnapshotsEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const ScreenSampleShipmentReceptionForm = () => {
  const { t } = useTranslation();
  const { reference } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sampleShipment = useSelector(
    (state) => state.sampleShipment.sampleShipment || {}
  );
  const center = useShipmentCenter(sampleShipment.centerId);
  const study = useSelector((state) => state.study.study);
  const user = useSelector((state) => state.user.user);
  const adminUser = user?.role === "admin";

  const [storageDatetime, setStorageDatetime] = React.useState(null);
  const [storageChamber, setStorageChamber] = React.useState("");
  const [storageShelf, setStorageShelf] = React.useState("");
  const [comments, setComments] = React.useState("");
  const [temperatureProbe, setTemperatureProbe] = React.useState(false);
  const [sonde, setSonde] = React.useState("");
  const [shipmentConforme, setShipmentConforme] = React.useState(false);
  const [studyTemperatureOptions, setStudyTemperatureOptions] =
    React.useState([]);
  const [receivedByTemperature, setReceivedByTemperature] = React.useState({});
  const [conformeByTemperature, setConformeByTemperature] = React.useState({});
  const [itemCommentsByTemperature, setItemCommentsByTemperature] =
    React.useState({});
  const [commentOpenByTemperature, setCommentOpenByTemperature] =
    React.useState({});
  const [isSavingReception, setIsSavingReception] = React.useState(false);
  const receptionSectionRef = React.useRef(null);
  const [initializedReceptionSourceKey, setInitializedReceptionSourceKey] =
    React.useState(null);
  const [requestTracking, setRequestTracking] = React.useState({
    name: "",
    email: "",
    date: "",
  });
  const [confirmTracking, setConfirmTracking] = React.useState({
    name: "",
    email: "",
    date: "",
  });
  const [receiveTracking, setReceiveTracking] = React.useState({
    name: "",
    email: "",
    date: "",
  });
  const effectiveStudyId =
    sampleShipment.studyId ||
    sampleShipment.StudyId ||
    sampleShipment.study?.id ||
    center?.studyId ||
    location.state?.studyId;

  React.useEffect(() => {
    if (reference) {
      store.dispatch(getSampleShipmentByReference(reference));
    }
  }, [reference]);

  React.useEffect(() => {
    if (effectiveStudyId) {
      store.dispatch(getStudyInfo(effectiveStudyId));
    }
  }, [effectiveStudyId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadStudyTemperatures = async () => {
      if (!effectiveStudyId) {
        setStudyTemperatureOptions([]);
        return;
      }

      try {
        const studyTemperatures = await getTemperaturesByStudy(effectiveStudyId);
        if (!isMounted) return;

        setStudyTemperatureOptions(
          uniqueTemperatures(
            (studyTemperatures || []).map(getTemperatureSettingName),
          ),
        );
      } catch (error) {
        console.error("Failed to load study temperatures:", error);
        if (isMounted) {
          setStudyTemperatureOptions([]);
        }
      }
    };

    loadStudyTemperatures();

    return () => {
      isMounted = false;
    };
  }, [effectiveStudyId]);

  React.useEffect(() => {
    const pickupAddress =
      sampleShipment.pickupAddress || sampleShipment.address || {};

    setRequestTracking({
      name:
        pickupAddress.contactName ||
        formatTrackingName(sampleShipment.createdByUser) ||
        "",
      email:
        pickupAddress.contactEmail ||
        sampleShipment.createdByUser?.email ||
        "",
      date: sampleShipment.createdAt || sampleShipment.sendingDate || "",
    });
    setConfirmTracking({
      name: formatTrackingName(sampleShipment.confirmedByUser) || "",
      email: sampleShipment.confirmedByUser?.email || "",
      date: sampleShipment.confirmedDatetime || "",
    });
    setReceiveTracking({
      name:
        formatTrackingName(sampleShipment.receiveByUser) ||
        formatTrackingName(sampleShipment.reception?.receiveByUser) ||
        "",
      email:
        sampleShipment.receiveByUser?.email ||
        sampleShipment.reception?.receiveByUser?.email ||
        "",
      date:
        sampleShipment.receiveDatetime ||
        sampleShipment.reception?.receiveDatetime ||
        "",
    });
  }, [
    sampleShipment.address,
    sampleShipment.confirmedByUser,
    sampleShipment.confirmedDatetime,
    sampleShipment.createdAt,
    sampleShipment.createdByUser,
    sampleShipment.pickupAddress,
    sampleShipment.receiveByUser,
    sampleShipment.receiveDatetime,
    sampleShipment.reception,
    sampleShipment.sendingDate,
  ]);

  const shipmentSamples = React.useMemo(() => {
    if (
      Array.isArray(sampleShipment.shipmentSamples) &&
      sampleShipment.shipmentSamples.length
    ) {
      return sampleShipment.shipmentSamples;
    }

    if (Array.isArray(sampleShipment.samples) && sampleShipment.samples.length) {
      return sampleShipment.samples;
    }

    return (sampleShipment.patients || []).flatMap(
      (patient) => patient.samples || []
    );
  }, [
    sampleShipment.patients,
    sampleShipment.samples,
    sampleShipment.shipmentSamples,
  ]);

  const shippingTemperatureSummary = React.useMemo(
    () =>
      buildShippingTemperatureSummary(
        shipmentSamples,
        studyTemperatureOptions,
      ),
    [shipmentSamples, studyTemperatureOptions]
  );

  const savedReception = sampleShipment.reception || null;
  const savedReceptionItemsByTemperature = new Map(
    (savedReception?.items || []).map((item) => [
      normalizeShippingTemperature(item.shippingTemperature),
      item,
    ]),
  );
  const sourcePickupAddress =
    sampleShipment.pickupAddress || sampleShipment.address || {};
  const receptionSourceSnapshot = buildComparableReceptionSnapshot({
    storageDatetime: savedReception?.storageDatetime,
    storageChamber: savedReception?.storageChamber,
    storageShelf: savedReception?.storageShelf,
    comments: savedReception?.comments,
    temperatureProbe: savedReception
      ? savedReception.temperatureProbe
      : sampleShipment.temperatureProbe,
    sonde: savedReception?.sonde,
    conforme: savedReception?.conforme,
    items: shippingTemperatureSummary.map(({ temperature, total }) => {
      const savedItem = savedReceptionItemsByTemperature.get(temperature);

      return {
        shippingTemperature: temperature,
        quantityReceived: savedItem
          ? savedItem.quantityReceived
          : String(total),
        conforme: savedItem?.conforme || false,
        comments: savedItem?.comments || "",
      };
    }),
    requestTracking: {
      name:
        sourcePickupAddress.contactName ||
        formatTrackingName(sampleShipment.createdByUser) ||
        "",
      email:
        sourcePickupAddress.contactEmail ||
        sampleShipment.createdByUser?.email ||
        "",
      date: sampleShipment.createdAt || sampleShipment.sendingDate || "",
    },
    confirmTracking: {
      name: formatTrackingName(sampleShipment.confirmedByUser) || "",
      email: sampleShipment.confirmedByUser?.email || "",
      date: sampleShipment.confirmedDatetime || "",
    },
    receiveTracking: {
      name:
        formatTrackingName(sampleShipment.receiveByUser) ||
        formatTrackingName(savedReception?.receiveByUser) ||
        "",
      email:
        sampleShipment.receiveByUser?.email ||
        savedReception?.receiveByUser?.email ||
        "",
      date:
        sampleShipment.receiveDatetime ||
        savedReception?.receiveDatetime ||
        "",
    },
  });
  const receptionSourceKey = JSON.stringify(receptionSourceSnapshot);

  React.useEffect(() => {
    setReceivedByTemperature((current) => {
      const next = { ...current };

      shippingTemperatureSummary.forEach(({ temperature, total }) => {
        if (next[temperature] === undefined) {
          next[temperature] = String(total);
        }
      });

      return next;
    });

    setConformeByTemperature((current) => {
      const next = { ...current };

      shippingTemperatureSummary.forEach(({ temperature }) => {
        if (next[temperature] === undefined) {
          next[temperature] = false;
        }
      });

      return next;
    });

    setItemCommentsByTemperature((current) => {
      const next = { ...current };

      shippingTemperatureSummary.forEach(({ temperature }) => {
        if (next[temperature] === undefined) {
          next[temperature] = "";
        }
      });

      return next;
    });

    setCommentOpenByTemperature((current) => {
      const next = { ...current };

      shippingTemperatureSummary.forEach(({ temperature }) => {
        if (next[temperature] === undefined) {
          next[temperature] = false;
        }
      });

      return next;
    });
  }, [shippingTemperatureSummary]);

  React.useEffect(() => {
    const reception = sampleShipment.reception;
    if (!sampleShipment.id) return;

    if (!reception) {
      setStorageDatetime(null);
      setStorageChamber("");
      setStorageShelf("");
      setComments("");
      setTemperatureProbe(toBooleanValue(sampleShipment.temperatureProbe));
      setSonde("");
      setShipmentConforme(false);
      setReceivedByTemperature(
        shippingTemperatureSummary.reduce(
          (next, { temperature, total }) => ({
            ...next,
            [temperature]: String(total),
          }),
          {},
        ),
      );
      setConformeByTemperature(
        shippingTemperatureSummary.reduce(
          (next, { temperature }) => ({
            ...next,
            [temperature]: false,
          }),
          {},
        ),
      );
      setItemCommentsByTemperature(
        shippingTemperatureSummary.reduce(
          (next, { temperature }) => ({
            ...next,
            [temperature]: "",
          }),
          {},
        ),
      );
      setCommentOpenByTemperature(
        shippingTemperatureSummary.reduce(
          (next, { temperature }) => ({
            ...next,
            [temperature]: false,
          }),
          {},
        ),
      );
      setInitializedReceptionSourceKey(receptionSourceKey);
      return;
    }

    setStorageDatetime(toDateOrNull(reception.storageDatetime));
    setStorageChamber(reception.storageChamber || "");
    setStorageShelf(reception.storageShelf || "");
    setComments(reception.comments || "");
    setTemperatureProbe(toBooleanValue(reception.temperatureProbe));
    setSonde(reception.sonde || "");
    setShipmentConforme(toBooleanValue(reception.conforme));

    setReceivedByTemperature((current) => {
      const next = { ...current };

      (reception.items || []).forEach((item) => {
        const temperature = normalizeShippingTemperature(
          item.shippingTemperature,
        );
        next[temperature] =
          item.quantityReceived === null || item.quantityReceived === undefined
            ? ""
            : String(item.quantityReceived);
      });

      return next;
    });

    setConformeByTemperature((current) => {
      const next = { ...current };

      (reception.items || []).forEach((item) => {
        const temperature = normalizeShippingTemperature(
          item.shippingTemperature,
        );
        next[temperature] = Boolean(item.conforme);
      });

      return next;
    });

    setItemCommentsByTemperature((current) => {
      const next = { ...current };

      (reception.items || []).forEach((item) => {
        const temperature = normalizeShippingTemperature(
          item.shippingTemperature,
        );
        next[temperature] = item.comments || "";
      });

      return next;
    });

    setCommentOpenByTemperature((current) => {
      const next = { ...current };

      (reception.items || []).forEach((item) => {
        const temperature = normalizeShippingTemperature(
          item.shippingTemperature,
        );
        next[temperature] = Boolean(item.comments);
      });

      return next;
    });
    setInitializedReceptionSourceKey(receptionSourceKey);
  }, [
    receptionSourceKey,
    sampleShipment.id,
    sampleShipment.reception,
    sampleShipment.temperatureProbe,
    shippingTemperatureSummary,
  ]);

  const centerNumber =
    sampleShipment.address?.centerNumber ||
    sampleShipment.pickupAddress?.centerNumber ||
    center?.centerNumber ||
    sampleShipment.centerId;
  const shipmentNumber = sampleShipment.reference || reference || "";
  const carrier = sampleShipment.carrier || "";
  const airwaybill =
    sampleShipment.trackingNumber || sampleShipment.airwaybill || "";
  const numberOfPackages = sampleShipment.numberOfPackages || "";
  const cdlStudyCode =
    study?.cdlStudyCode ||
    sampleShipment.studyCode ||
    location.state?.studyCode ||
    center?.Study?.cdlStudyCode ||
    "-";
  const normalizedShipmentStatus = String(sampleShipment.status || "")
    .trim()
    .toLowerCase();
  const receptionConfirmed =
    Boolean(sampleShipment.receiveDatetime) ||
    normalizedShipmentStatus === "received";
  const canSaveReception =
    normalizedShipmentStatus === "confirmed" || receptionConfirmed;
  const currentReceptionSnapshot = buildComparableReceptionSnapshot({
    storageDatetime,
    storageChamber,
    storageShelf,
    comments,
    temperatureProbe,
    sonde,
    conforme: shipmentConforme,
    items: shippingTemperatureSummary.map(({ temperature }) => ({
      shippingTemperature: temperature,
      quantityReceived: receivedByTemperature[temperature],
      conforme: conformeByTemperature[temperature],
      comments: itemCommentsByTemperature[temperature],
    })),
    requestTracking,
    confirmTracking,
    receiveTracking,
  });
  const hasReceptionChanges =
    Boolean(sampleShipment.id) &&
    initializedReceptionSourceKey === receptionSourceKey &&
    !areReceptionSnapshotsEqual(
      currentReceptionSnapshot,
      receptionSourceSnapshot,
    );

  const printableReceptionShipment = React.useMemo(
    () => ({
      ...sampleShipment,
      centerNumber,
      airwaybill,
      numberOfPackages,
      reception: {
        ...(sampleShipment.reception || {}),
        storageDatetime,
        storageChamber,
        storageShelf,
        temperatureProbe,
        sonde,
        conforme: shipmentConforme,
        comments,
        items: shippingTemperatureSummary.map(({ temperature, total }) => ({
          shippingTemperature: temperature,
          quantitySent: total,
          quantityReceived:
            receivedByTemperature[temperature] === "" ||
            receivedByTemperature[temperature] === undefined ||
            receivedByTemperature[temperature] === null
              ? null
              : Number(receivedByTemperature[temperature]),
          conforme: Boolean(conformeByTemperature[temperature]),
          comments: itemCommentsByTemperature[temperature] || null,
        })),
      },
    }),
    [
      airwaybill,
      centerNumber,
      comments,
      conformeByTemperature,
      itemCommentsByTemperature,
      numberOfPackages,
      receivedByTemperature,
      sampleShipment,
      shipmentConforme,
      shippingTemperatureSummary,
      sonde,
      storageChamber,
      storageDatetime,
      storageShelf,
      temperatureProbe,
    ],
  );

  const handleReceivedChange = (temperature, value) => {
    if (/^\d*$/.test(value)) {
      setReceivedByTemperature((current) => ({
        ...current,
        [temperature]: value,
      }));
    }
  };

  const handleConformeChange = (temperature, checked) => {
    setConformeByTemperature((current) => ({
      ...current,
      [temperature]: checked,
    }));
  };

  const handleToggleItemComment = (temperature) => {
    const isOpen = Boolean(commentOpenByTemperature[temperature]);

    if (isOpen) {
      setItemCommentsByTemperature((current) => ({
        ...current,
        [temperature]: "",
      }));
    }

    setCommentOpenByTemperature((current) => ({
      ...current,
      [temperature]: !isOpen,
    }));
  };

  const handleItemCommentChange = (temperature, value) => {
    setItemCommentsByTemperature((current) => ({
      ...current,
      [temperature]: value,
    }));
  };

  const handleBackClick = () => {
    if (shipmentNumber) {
      navigate(`/sample-shipments/${encodeURIComponent(shipmentNumber)}`, {
        state: location.state,
      });
      return;
    }

    navigate(-1);
  };

  const handleBiobankingClick = () => {
    if (!shipmentNumber) return;
    navigate(`/sample-shipments/${encodeURIComponent(shipmentNumber)}/biobank`, {
      state: location.state,
    });
  };

  const handleShipmentStepClick = (hash) => {
    navigate(`/sample-shipments/${encodeURIComponent(reference)}${hash}`, {
      state: { ...location.state, sampleShipmentId: sampleShipment.id },
    });
  };

  const canNavigateShipmentSteps =
    Boolean(reference) &&
    sampleShipment.reference === reference &&
    !hasReceptionChanges &&
    !isSavingReception;

  const handleDiscardChanges = () => {
    window.location.reload();
  };

  const handleConfirmClick = async () => {
    if (!sampleShipment.id) return;

    try {
      setIsSavingReception(true);
      const receiveDatetime =
        receiveTracking.date ||
        sampleShipment.reception?.receiveDatetime ||
        sampleShipment.receiveDatetime ||
        new Date().toISOString();

      if (adminUser) {
        await store.dispatch(
          editSampleShipment(sampleShipment.id, {
            pickupAddress: {
              ...(sampleShipment.pickupAddress || sampleShipment.address || {}),
              contactName: requestTracking.name,
              contactEmail: requestTracking.email,
            },
            createdAt: requestTracking.date || sampleShipment.createdAt,
            confirmedDatetime:
              confirmTracking.date || sampleShipment.confirmedDatetime || null,
            receiveDatetime,
          }),
        );
      }

      await upsertSampleShipmentReception(sampleShipment.id, {
        receiveDatetime,
        receiveByUserId:
          sampleShipment.reception?.receiveByUserId ||
          sampleShipment.receiveByUserId ||
          user?.id,
        storageDatetime,
        storageChamber,
        storageShelf,
        temperatureProbe,
        sonde,
        conforme: shipmentConforme,
        comments,
        items: shippingTemperatureSummary.map(({ temperature }) => ({
          shippingTemperature: temperature,
          quantityReceived:
            receivedByTemperature[temperature] === "" ||
            receivedByTemperature[temperature] === undefined ||
            receivedByTemperature[temperature] === null
              ? null
              : Number(receivedByTemperature[temperature]),
          conforme: Boolean(conformeByTemperature[temperature]),
          comments: itemCommentsByTemperature[temperature] || null,
        })),
      });

      showSuccessToast(
        receptionConfirmed
          ? t("sampleShipmentReceptionUpdated", "Reception updated")
          : t("sampleShipmentReceptionSaved", "Reception saved"),
      );

      if (shipmentNumber) {
        store.dispatch(getSampleShipmentByReference(shipmentNumber));
      }
    } catch (error) {
      console.error("Error receiving sample shipment:", error);
      showErrorAlert(
        t(
          "sampleShipmentReceptionSaveFailed",
          "Failed to save reception information",
        ),
      );
    } finally {
      setIsSavingReception(false);
    }
  };

  const handleUndoReceptionClick = async () => {
    if (!sampleShipment.id) return;

    const result = await Swal.fire({
      title: t("undoReception", "Undo reception?"),
      text: t(
        "undoReceptionConfirm",
        "Reception data will be deleted and the shipment will return to confirmed status.",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("undo", "Undo"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#1e3179",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setIsSavingReception(true);
      await deleteSampleShipmentReception(sampleShipment.id);
      showSuccessToast(
        t("sampleShipmentReceptionUndone", "Reception undone"),
      );

      if (shipmentNumber) {
        store.dispatch(getSampleShipmentByReference(shipmentNumber));
      }
    } catch (error) {
      console.error("Error undoing sample shipment reception:", error);
      showErrorAlert(
        t(
          "sampleShipmentReceptionUndoFailed",
          "Failed to undo reception information",
        ),
      );
    } finally {
      setIsSavingReception(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader
          title={t("sampleShipmentReception", "Sample Shipment Reception")}
          downloadLabel={t("shipmentReception", "Shipment Reception")}
          onBack={handleBackClick}
          onDownload={() =>
            printSampleShipmentReception(printableReceptionShipment, study)
          }
          downloadDisabled={!sampleShipment.id}
        />

        <SampleShipmentSteps
          activeStep={2}
          status={
            sampleShipment.reference === reference ? normalizedShipmentStatus : ""
          }
          onConfirm={
            canNavigateShipmentSteps
              ? () => handleShipmentStepClick("#shipment-confirmation")
              : undefined
          }
          onReceive={() => {
            receptionSectionRef.current?.focus({ preventScroll: true });
            receptionSectionRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }}
        />

        <Paper
          ref={receptionSectionRef}
          tabIndex={-1}
          variant="outlined"
          sx={{ ...sectionSx, scrollMarginTop: 24 }}>
          <SectionHeader
            title={t("shipmentInformation", "Shipment Information")}
            description={t(
              "shipmentInformationReception",
              "Shipment reference and transport document"
            )}
            action={
              <StudyCodeLabelHelper
                label={t("cdlStudyCode", "CDL Study Code")}
                value={cdlStudyCode}
              />
            }
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ReadOnlyField label={t("centerId")} value={centerNumber} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadOnlyField label={t("shipmentNumber")} value={shipmentNumber} />
            </Grid>
            <Grid item xs={12} md={3}>
              <ReadOnlyField label={t("carrier")} value={carrier} />
            </Grid>
            <Grid item xs={12} md={3}>
              <ReadOnlyField label={t("airwaybill")} value={airwaybill} />
            </Grid>
            <Grid item xs={12} md={3}>
              <ReadOnlyField
                label={t("numberOfPackages")}
                value={numberOfPackages}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CheckboxField
                label={t("conforme", "Conforme")}
                checked={shipmentConforme}
                onChange={setShipmentConforme}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CheckboxField
                label={t("temperatureProbe", "Temperature probe")}
                checked={temperatureProbe}
                onChange={setTemperatureProbe}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("sonde", "Sonde")}
                value={sonde}
                onChange={(event) => setSonde(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("comments")}
                value={comments}
                onChange={(event) => setComments(event.target.value)}
                fullWidth
              />
            </Grid>
            {/*
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: "flex", alignItems: "stretch" }}>
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                disabled={!airwaybill || !airwaybillDocumentActionsEnabled}
                fullWidth
                sx={{ minHeight: 56 }}>
                {t("viewAirwaybill", "View airwaybill")}
              </Button>
            </Grid>
            */}
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("samplesReception", "Samples Reception")}
            description={t(
              "samplesReceptionByTemperature",
              "Received quantities by shipping temperature"
            )}
          />
          <TableContainer sx={tableSx}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("shippingTemperature")}</TableCell>
                  <TableCell>
                    {t(
                      "totalSamplesSentTheory",
                      "Total Samples sent (theory)",
                    )}
                  </TableCell>
                  <TableCell>
                    {t("totalSamplesReceived", "Total Samples received")}
                  </TableCell>
                  <TableCell align="center">{t("conforme", "Conforme")}</TableCell>
                  <TableCell align="right">{t("comment", "Comment")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shippingTemperatureSummary.map((item) => (
                  <React.Fragment key={item.temperature}>
                    <TableRow>
                      <TableCell>{item.temperature}</TableCell>
                      <TableCell>{item.total}</TableCell>
                      <TableCell sx={{ width: { xs: 140, sm: 220 } }}>
                        <TextField
                          type="number"
                          value={receivedByTemperature[item.temperature] ?? ""}
                          onChange={(event) =>
                            handleReceivedChange(
                              item.temperature,
                              event.target.value
                            )
                          }
                          size="small"
                          fullWidth
                          inputProps={{ min: 0, step: 1 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={Boolean(
                            conformeByTemperature[item.temperature],
                          )}
                          onChange={(event) =>
                            handleConformeChange(
                              item.temperature,
                              event.target.checked
                            )
                          }
                          inputProps={{
                            "aria-label": `${item.temperature} ${t(
                              "conforme",
                              "Conforme"
                            )}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handleToggleItemComment(item.temperature)
                          }>
                          {commentOpenByTemperature[item.temperature]
                            ? t("remove", "Remove")
                            : t("add", "Add")}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {commentOpenByTemperature[item.temperature] && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            bgcolor: "grey.50",
                            borderTop: "1px solid",
                            borderColor: "divider",
                            px: 2,
                            py: 1,
                          }}>
                          <TextField
                            label={t("comment", "Comment")}
                            value={
                              itemCommentsByTemperature[item.temperature] || ""
                            }
                            onChange={(event) =>
                              handleItemCommentChange(
                                item.temperature,
                                event.target.value,
                              )
                            }
                            size="small"
                            fullWidth
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("storage", "Storage")}
            description={t(
              "storageReception",
              "Storage information after reception",
            )}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("storageDatetime", "Storage date/time")}
                type="datetime-local"
                value={toDateTimeInputValue(storageDatetime)}
                onChange={(event) =>
                  setStorageDatetime(
                    fromDateTimeInputValue(event.target.value),
                  )
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("storageChamber", "Storage chamber")}
                value={storageChamber}
                onChange={(event) => setStorageChamber(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("storageShelf", "Storage shelf")}
                value={storageShelf}
                onChange={(event) => setStorageShelf(event.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <SampleShipmentAudit
          sampleShipment={sampleShipment}
          sectionSx={sectionSx}
          editable={adminUser}
          trackingValues={{
            request: requestTracking,
            confirm: confirmTracking,
            receive: receiveTracking,
          }}
          onTrackingChange={{
            request: setRequestTracking,
            confirm: setConfirmTracking,
            receive: setReceiveTracking,
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: receptionConfirmed ? "space-between" : "center",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            pb: 2,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}>
            <Tooltip
              title={
                receptionConfirmed
                  ? t(
                      "updateReceptionTooltip",
                      "Save the pending changes to the reception.",
                    )
                  : t("confirmReception", "Confirm Reception")
              }
              arrow>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <Button
                  variant={receptionConfirmed ? "outlined" : "contained"}
                  color="primary"
                  onClick={handleConfirmClick}
                  disabled={
                    !sampleShipment.id ||
                    !canSaveReception ||
                    isSavingReception ||
                    (receptionConfirmed && !hasReceptionChanges)
                  }
                  sx={{
                    minWidth: receptionConfirmed ? 180 : 260,
                    minHeight: 48,
                    width: { xs: "100%", sm: "auto" },
                  }}>
                  {isSavingReception
                    ? t("saving", "Saving...")
                    : receptionConfirmed
                      ? t("update", "Update")
                      : t("confirmReception", "Confirm Reception")}
                </Button>
              </Box>
            </Tooltip>
            {receptionConfirmed && hasReceptionChanges && (
              <Tooltip
                title={t("discardChanges", "Discard changes")}
                arrow>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleDiscardChanges}
                  sx={{
                    minHeight: 48,
                    bgcolor: "grey.100",
                    borderColor: "grey.300",
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: "grey.200",
                      borderColor: "grey.400",
                    },
                  }}>
                  {t("discard", "Discard")}
                </Button>
              </Tooltip>
            )}
          </Box>
          {receptionConfirmed && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                width: { xs: "100%", sm: "auto" },
              }}>
              <Tooltip
                title={t(
                  "undoReceptionTooltip",
                  "Undo the reception if it was confirmed by accident.",
                )}
                arrow>
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleUndoReceptionClick}
                    disabled={!sampleShipment.id || isSavingReception}
                    sx={{
                      minWidth: 180,
                      minHeight: 48,
                      width: { xs: "100%", sm: "auto" },
                    }}>
                    {t("undo", "Undo")}
                  </Button>
                </Box>
              </Tooltip>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBiobankingClick}
                disabled
                sx={{
                  minWidth: 180,
                  minHeight: 48,
                  width: { xs: "100%", sm: "auto" },
                }}>
                {t("bioBanking", "Bio Banking")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default ScreenSampleShipmentReceptionForm;
